import { getAccount, waitForTransactionReceipt, writeContract } from "@wagmi/core";
import { getWagmiConfig } from "./wallet/reown.js";
import { getStakingEnv } from "./lib/env.js";
import { erc721Abi, stakingAbi } from "./lib/abis.js";
import {
  clearStakedTokenIdsCache,
  readCanStake,
  readIsApprovedForAll,
  readStakingPaused,
  stakingAddressesOrNull,
} from "./lib/stakingData.js";
import { getSelectedWalletTokenIds } from "./nftGallery.js";
import { getSelectedRoostTokenIds } from "./roostGallery.js";
import { showToast } from "./lib/toast.js";
import { formatTxError } from "./lib/txErrors.js";
import { clearOwnedNftCache } from "./lib/alchemyNfts.js";

export async function runStake() {
  const cfg = getWagmiConfig();
  if (!cfg) {
    showToast("Wallet not ready.", { variant: "info" });
    return;
  }
  const { address } = getAccount(cfg);
  if (!address) {
    showToast("Connect your wallet first.", { variant: "info" });
    return;
  }
  const tokenIds = getSelectedWalletTokenIds();
  if (tokenIds.length === 0) {
    showToast("Select one or more Owlex in the Wallet tab.", { variant: "info" });
    return;
  }

  const env = getStakingEnv();
  const nft = env.owlexNft;
  const { staking } = stakingAddressesOrNull();
  if (!staking || !/^0x[a-fA-F0-9]{40}$/.test(nft)) {
    showToast("Missing contract addresses in env.", { variant: "info" });
    return;
  }

  const stakeBtn = document.getElementById("btn-stake");
  const prev = stakeBtn?.textContent;
  if (stakeBtn) {
    stakeBtn.disabled = true;
    stakeBtn.textContent = "Checking…";
  }

  try {
    const [paused, canStake, approved] = await Promise.all([
      readStakingPaused(cfg, staking),
      readCanStake(cfg, staking, address),
      readIsApprovedForAll(cfg, nft, address, staking),
    ]);

    if (paused) {
      showToast("Staking is paused.", { variant: "info" });
      return;
    }
    if (!canStake) {
      showToast("Cooldown active — wait before staking again.", { variant: "info" });
      return;
    }

    if (stakeBtn) stakeBtn.textContent = "Confirm…";

    if (!approved) {
      const h1 = await writeContract(cfg, {
        address: nft,
        abi: erc721Abi,
        functionName: "setApprovalForAll",
        args: [staking, true],
        chainId: 1,
      });
      await waitForTransactionReceipt(cfg, { hash: h1 });
    }

    const tids = tokenIds.map((id) => BigInt(id));
    if (tids.length === 1) {
      const h2 = await writeContract(cfg, {
        address: staking,
        abi: stakingAbi,
        functionName: "stake",
        args: [tids[0]],
        chainId: 1,
      });
      await waitForTransactionReceipt(cfg, { hash: h2 });
    } else {
      const h2 = await writeContract(cfg, {
        address: staking,
        abi: stakingAbi,
        functionName: "stakeBatch",
        args: [tids],
        chainId: 1,
      });
      await waitForTransactionReceipt(cfg, { hash: h2 });
    }
  } catch (err) {
    console.error("[owlex-staking] stake", err);
    showToast(formatTxError(err), { variant: "error" });
  } finally {
    if (stakeBtn) {
      stakeBtn.textContent = prev ?? "Stake";
    }
    const { address: a } = getAccount(cfg);
    clearOwnedNftCache(a ?? null);
    clearStakedTokenIdsCache(a ?? null);
    window.dispatchEvent(
      new CustomEvent("owlex:staking-refresh", { detail: { address: a } })
    );
  }
}

export async function runUnstake() {
  const cfg = getWagmiConfig();
  if (!cfg) {
    showToast("Wallet not ready.", { variant: "info" });
    return;
  }
  const { address } = getAccount(cfg);
  if (!address) {
    showToast("Connect your wallet first.", { variant: "info" });
    return;
  }
  const tokenIds = getSelectedRoostTokenIds();
  if (tokenIds.length === 0) {
    showToast("Select one or more Owlex in the Roost tab.", { variant: "info" });
    return;
  }

  for (const tokenId of tokenIds) {
    const card = document.querySelector(
      `.nft-card--roost[data-token-id="${tokenId}"]`
    );
    if (card?.getAttribute("data-can-unstake") !== "true") {
      showToast("Still roosting — wait until the timer ends for every selected NFT.", {
        variant: "info",
      });
      return;
    }
  }

  const { staking } = stakingAddressesOrNull();
  if (!staking) {
    showToast("Missing contract addresses in env.", { variant: "info" });
    return;
  }

  const unstakeBtn = document.getElementById("btn-unstake");
  const prev = unstakeBtn?.textContent;
  if (unstakeBtn) {
    unstakeBtn.disabled = true;
    unstakeBtn.textContent = "Checking…";
  }

  try {
    const paused = await readStakingPaused(cfg, staking);
    if (paused) {
      showToast("Staking is paused.", { variant: "info" });
      return;
    }

    if (unstakeBtn) unstakeBtn.textContent = "Confirm…";

    const tids = tokenIds.map((id) => BigInt(id));
    if (tids.length === 1) {
      const h = await writeContract(cfg, {
        address: staking,
        abi: stakingAbi,
        functionName: "unstake",
        args: [tids[0]],
        chainId: 1,
      });
      await waitForTransactionReceipt(cfg, { hash: h });
    } else {
      const h = await writeContract(cfg, {
        address: staking,
        abi: stakingAbi,
        functionName: "unstakeBatch",
        args: [tids],
        chainId: 1,
      });
      await waitForTransactionReceipt(cfg, { hash: h });
    }
  } catch (err) {
    console.error("[owlex-staking] unstake", err);
    showToast(formatTxError(err), { variant: "error" });
  } finally {
    if (unstakeBtn) {
      unstakeBtn.textContent = prev ?? "Unstake";
    }
    const { address: a } = getAccount(cfg);
    clearOwnedNftCache(a ?? null);
    clearStakedTokenIdsCache(a ?? null);
    window.dispatchEvent(
      new CustomEvent("owlex:staking-refresh", { detail: { address: a } })
    );
  }
}
